import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Stack, Text } from 'components'
import { useArcTranslation, useMantineTheme, useMediaQuery } from 'hooks'
import { useCurrentUser } from 'context'
import { StudentSelector } from './StudentSelector'
import { TrackerSkeleton } from './TrackerSkeleton'
import { TrackerStackedList } from './TrackerStackedList'
import { TrackerTable } from './TrackerTable'
import { useDevelopmentTracker, useTrackerStudents } from './useDevelopmentTracker'
import { ALL_STUDENTS, type StudentFilter } from './types'

export const DevelopmentTrackerTab = () => {
  const { id: projectId } = useParams()
  const { t } = useArcTranslation()
  const { currentUser } = useCurrentUser()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isStudent = Boolean(currentUser?.student_account)
  const showSelector = !isStudent

  const [selected, setSelected] = useState<StudentFilter>(ALL_STUDENTS)
  const studentsQuery = useTrackerStudents(projectId, showSelector)
  const tracker = useDevelopmentTracker(projectId, selected)

  if (tracker.isLoading) return <TrackerSkeleton withSelector={showSelector} />

  if (tracker.isError) return <Alert color='red'>{t('arc_development_tracker.error_generic')}</Alert>

  const emptyText = isStudent
    ? t('arc_development_tracker.empty_student')
    : t('arc_development_tracker.empty_teacher')

  // Spec: only items with at least one recorded teacher score are shown.
  const scoredItems = tracker.items.filter(
    item => item.assessmentAverage != null && item.assessmentAverage > 0,
  )

  return (
    <Stack spacing='lg'>
      {showSelector && (
        <StudentSelector
          students={studentsQuery.students}
          value={selected}
          onChange={setSelected}
          disabled={studentsQuery.isLoading}
        />
      )}
      {scoredItems.length === 0 ? (
        <Text color='dimmed' size='sm' align='center' py='xl'>
          {emptyText}
        </Text>
      ) : isMobile ? (
        <TrackerStackedList items={scoredItems} />
      ) : (
        <TrackerTable items={scoredItems} />
      )}
    </Stack>
  )
}
